import React from 'react';
import { AlertBanner, InputTextArea, TagGeneral, Text } from '@spark';

const CHIP_ROW_STYLE = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--dimension-spacing-08)',
};

const STACK_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dimension-spacing-16)',
    width: '100%',
};

const MATCH_BANNER_TEXT =
    'Note: AI annotates only unreviewed records. Training uses reviewed records and AI-generated labels from unreviewed records';

export function hasPromptText(value) {
    return Boolean(String(value ?? '').trim());
}

export function labelsFromUsecaseCounts(usecase) {
    const counts = usecase?.label_names_count || {};
    return Object.entries(counts).map(([name, info]) => {
        const total = typeof info === 'number' ? info : (info?.total ?? 0);
        return { name, total };
    });
}

export default function ImproveFlowAiStep({
    usecase,
    prompt,
    systemPrompt,
    totalRecords,
}) {
    const chips = labelsFromUsecaseCounts(usecase);
    const labelCount = chips.length;
    // Training uses reviewed human labels plus AI labels on unreviewed rows,
    // so prefer the actual record total over the per-label sum, which only
    // counts rows that currently have labels.
    const labeledRecordCount = chips.reduce((sum, chip) => sum + (chip.total || 0), 0);
    const recordCount = Number(totalRecords) > 0 ? Number(totalRecords) : labeledRecordCount;
    const consideredHeading = `Labels that will be considered (${labelCount} labels · ${recordCount} records)`;
    const showSystemPrompt = hasPromptText(usecase?.teacher_system_prompt);
    const showUserPrompt = hasPromptText(usecase?.prompt);

    return (
        <div style={STACK_STYLE}>
            {chips.length > 0 ? (
                <>
                    <Text variant="heading-5-semibold">{consideredHeading}</Text>
                    <div style={CHIP_ROW_STYLE}>
                        {chips.map((chip) => (
                            <TagGeneral
                                key={chip.name}
                                label={`${chip.name} (${chip.total})`}
                            />
                        ))}
                    </div>
                </>
            ) : (
                <Text variant="detail-regular" color="secondary">
                    No labels yet. The AI will use the labels in your task definition.
                </Text>
            )}
            <div style={STACK_STYLE}>
                <Text variant="heading-5-semibold">Validate the task definition</Text>
                <Text variant="detail-regular" color="secondary">
                    Confirm the prompt that will guide the AI as it annotates your data.
                </Text>
                {showSystemPrompt ? (
                    <InputTextArea
                        label="System prompt"
                        stretch
                        readOnly
                        rows={4}
                        style={{ width: '100%', maxWidth: '100%' }}
                        value={systemPrompt}
                    />
                ) : null}
                {showUserPrompt ? (
                    <InputTextArea
                        label="User prompt"
                        stretch
                        readOnly
                        rows={4}
                        style={{ width: '100%', maxWidth: '100%' }}
                        value={prompt}
                    />
                ) : null}
            </div>
            <AlertBanner
                feedback="info"
                text={(
                    <Text as="span" variant="detail-regular" style={{ whiteSpace: 'nowrap' }}>
                        {MATCH_BANNER_TEXT}
                    </Text>
                )}
                showButton={false}
                showCloseButton={false}
            />
        </div>
    );
}
